import {useEffect, useMemo, useState} from 'react';

import {getClassDetailHeaderQuery} from './ClassDetailHeader';

/**
 * True once the class's banner data is available. Reads the cache synchronously (`isResourceReady`)
 * so a revisit to a loaded class reveals instantly with no skeleton blink; a cold class shows the
 * skeleton until its query resolves. Re-arms when the class changes (#19132).
 */
export const useClassPageReady = (classroomId: string): boolean => {
  const query = useMemo(() => getClassDetailHeaderQuery(classroomId), [classroomId]);
  const [readyId, setReadyId] = useState<string | null>(() =>
    query.isResourceReady() ? classroomId : null
  );

  // Re-check synchronously on class change so a cached class never flashes the skeleton.
  const [trackedId, setTrackedId] = useState(classroomId);
  if (trackedId !== classroomId) {
    setTrackedId(classroomId);
    setReadyId(query.isResourceReady() ? classroomId : null);
  }

  // A cold class is revealed here once its query resolves (a cached one already revealed in render).
  useEffect(() => {
    let isStale = false;
    // Reveal on success or failure; an invalid class is handled by ClassDetailEntryFlows' redirect.
    const reveal = () => {
      if (!isStale) {
        setReadyId(classroomId);
      }
    };
    query.getResourcePromise().then(reveal).catch(reveal);
    return () => {
      isStale = true;
    };
  }, [query, classroomId]);

  return readyId === classroomId;
};
